import { useEffect, useState } from "react";
import {
  AppButton,
  AppTable,
  AppTableCell,
  AppTableHeadCell,
  AppTableRow,
  PageHeader,
} from "../../components/ui";
import {
  TextField,
  InputAdornment
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { ClioService, ClioTasksInfo } from "../../api";
import { useClioUsers } from "../../components/shared/ClioSelects";
import ClioTaskInfoDialog from "./ClioTaskInfoDialog";

const MySwal = withReactContent(Swal);

const TABLE_COLUMNS = [
  { id: "id", label: "ID", sortable: false },
  { id: "name", label: "Name", sortable: false },
  { id: "taskType", label: "Type", sortable: false },
  { id: "dueDays", label: "Due Days", sortable: false },
  { id: "description", label: "Description", sortable: false },
  { id: "assignee", label: "Assignee", sortable: false },
];

export default function ClioTaskDataUpdate() {
  const [allTasks, setAllTasks] = useState<ClioTasksInfo[]>([]);
  const { users } = useClioUsers(true);
  const [filteredTasks, setFilteredTasks] = useState<ClioTasksInfo[]>([]);
  const [selectedTask, setSelectedTask] = useState<ClioTasksInfo | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    ClioService.getApiClioTaskData().then((tasks) => {
        const sortedTasks = tasks.sort((t1, t2) => t1.name?.localeCompare(t2.name ?? "") ?? -1);
        setAllTasks(sortedTasks);
        setFilteredTasks(sortedTasks);
    });
  }, []);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredTasks(allTasks);
      return;
    }
    const lower = searchTerm.toLowerCase();
    setFilteredTasks(
      allTasks.filter(
        (t) =>
          t.name?.toLowerCase().includes(lower) ||
          (t.description ?? "").toLowerCase().includes(lower) ||
          t.assignee?.toLowerCase().includes(lower)
      )
    );
    setPage(0);
  }, [searchTerm, allTasks]);

  const handleUpdate = (updated: ClioTasksInfo) => {
    if (!updated?.id) return;

    ClioService.postApiClioTaskDataUpdate(updated.id, {
      name: updated.name,
      dueDays: updated.dueDays,
      description: updated.description,
      assignee: updated.assignee,
      isCalendarDate: updated.isCalendarDate
    })
      .then(() => {
        setAllTasks((prev) =>
            prev.map((task) => (task.id === updated.id ? updated : task))
        );

        // Also keep filteredTasks in sync
        setFilteredTasks((prev) =>
            prev.map((task) => (task.id === updated.id ? updated : task))
        );
        
        MySwal.fire({
            icon: "success",
            title: <p>Task updated successfully.</p>,
        })
      })
      .catch(() =>
        MySwal.fire({
          icon: "error",
          title: <p>Error updating task.</p>,
        })
      )
      .finally(() => setSelectedTask(null));
  };

  const pageRows = filteredTasks.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <div>
      <PageHeader
        title="Edit tasks"
        subtitle="Update Clio task details and configuration."
      />

      {/* Search bar */}
      <TextField
        placeholder="Search by name or description..."
        variant="outlined"
        fullWidth
        size="small"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        className="mb-3"
      />

      <AppTable
        columns={TABLE_COLUMNS}
        showRowNumber={false}
        trailingHeadCells={
          <AppTableHeadCell align="right">Action</AppTableHeadCell>
        }
        extraColumnCount={1}
        isEmpty={pageRows.length === 0}
        emptyTitle="No tasks found"
        pagination={{
          count: filteredTasks.length,
          page,
          rowsPerPage,
          rowsPerPageOptions: [10, 20, 50],
          onPageChange: setPage,
          onRowsPerPageChange: (next) => {
            setRowsPerPage(next);
            setPage(0);
          },
        }}
      >
        {pageRows.map((task) => (
          <AppTableRow key={task.id}>
            <AppTableCell>{task.id}</AppTableCell>
            <AppTableCell>{task.name}</AppTableCell>
            <AppTableCell>{task.taskType}</AppTableCell>
            <AppTableCell>{task.dueDays}</AppTableCell>
            <AppTableCell
              sx={{
                maxWidth: 250,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {task.description}
            </AppTableCell>
            <AppTableCell>{task.assignee}</AppTableCell>
            <AppTableCell align="right">
              <AppButton
                variant="outlined"
                size="small"
                onClick={() => setSelectedTask(task)}
              >
                Edit
              </AppButton>
            </AppTableCell>
          </AppTableRow>
        ))}
      </AppTable>

      {/* Edit dialog */}
      { selectedTask && <ClioTaskInfoDialog
        task={selectedTask}
        users={users}
        onClose={() => setSelectedTask(null)}
        onSave={handleUpdate}
      /> }
    </div>
  );
}
